import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { toast } from "sonner";

// Archive button (neutral/warning) + Permanent Delete button (destructive) for any admin record.
export const RowActions = ({ module, record, onDone, showRestore = false }) => {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const [dlg, setDlg] = useState(null); // 'archive' | 'delete'
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [deps, setDeps] = useState(null);

  const close = () => { setDlg(null); setReason(""); setConfirmText(""); setDeps(null); };

  const doArchive = async () => {
    if (!reason.trim()) { toast.error(t("archive_reason_required")); return; }
    setBusy(true);
    try {
      await api.post(`/admin/${module}/${record.id}/archive`, { reason });
      toast.success(t("archived_success")); close(); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const doRestore = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/${module}/${record.id}/restore`);
      toast.success(t("restored_success")); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const openDelete = async () => {
    setDlg("delete");
    try {
      const { data } = await api.get(`/admin/${module}/${record.id}/dependencies`);
      setDeps(data);
    } catch { setDeps(null); }
  };

  const doDelete = async () => {
    if (!reason.trim()) { toast.error(t("delete_reason_required")); return; }
    if (confirmText !== "DELETE") { toast.error(t("type_delete_confirm")); return; }
    setBusy(true);
    try {
      await api.delete(`/admin/${module}/${record.id}`, { data: { reason, confirm: confirmText } });
      toast.success(t("deleted_success")); close(); onDone && onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.dependencies) {
        setDeps({ can_delete: false, total: d.total, dependencies: d.dependencies });
        toast.error(t("cannot_delete_deps"));
      } else { toast.error((typeof d === "string" ? d : d?.message) || "Failed"); }
    } finally { setBusy(false); }
  };

  const blocked = deps && deps.can_delete === false;

  return (
    <>
      {showRestore ? (
        <button disabled={busy} onClick={doRestore} data-testid={`restore-${module}-${record.id}`}
          className="text-xs font-semibold text-emerald-600">{t("restore")}</button>
      ) : (
        <button disabled={busy} onClick={() => setDlg("archive")} data-testid={`archive-${module}-${record.id}`}
          className="text-xs font-semibold text-amber-600">{t("archive")}</button>
      )}
      <button disabled={busy} onClick={openDelete} data-testid={`delete-${module}-${record.id}`}
        className="text-xs font-bold text-red-600">{t("delete_permanently")}</button>

      {dlg === "archive" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="archive-dialog">
          <div className="w-full max-w-sm rounded-xl bg-white p-4 space-y-3">
            <h3 className="font-bold text-slate-800">{t("archive")} — {record.name || record.business_name || record.contact_name || record.id}</h3>
            <p className="text-xs text-slate-500">{module}</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("reason")}
              data-testid="archive-reason" className="w-full min-h-[80px] rounded-lg border border-slate-200 p-2 text-sm" />
            <div className="flex gap-2">
              <button onClick={close} className="flex-1 min-h-[44px] rounded-lg border border-slate-300 font-semibold">{t("cancel")}</button>
              <button onClick={doArchive} disabled={busy} data-testid="archive-confirm"
                className="flex-1 min-h-[44px] rounded-lg bg-amber-500 text-white font-semibold disabled:opacity-50">{busy ? "…" : t("archive")}</button>
            </div>
          </div>
        </div>
      )}

      {dlg === "delete" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="delete-dialog">
          <div className="w-full max-w-sm rounded-xl bg-white p-4 space-y-3">
            <h3 className="font-bold text-red-700">{t("delete_permanently")} — {record.name || record.business_name || record.contact_name || record.id}</h3>
            <p className="text-xs text-slate-500">{module}</p>
            <div className="rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-700">{t("permanent_delete_warning")}</div>
            {blocked && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800" data-testid="delete-deps">
                {t("cannot_delete_deps")}
                <ul className="mt-1 list-disc pl-4">
                  {deps.dependencies.map((d) => <li key={d.module}>{d.count} {t("dependencies_found")} {d.module}</li>)}
                </ul>
              </div>
            )}
            {!blocked && (
              <>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("reason")}
                  data-testid="delete-reason" className="w-full min-h-[60px] rounded-lg border border-slate-200 p-2 text-sm" />
                <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder='Type DELETE'
                  data-testid="delete-confirm-text" className="w-full min-h-[44px] rounded-lg border border-slate-300 px-3 text-sm font-bold" />
              </>
            )}
            <div className="flex gap-2">
              <button onClick={close} className="flex-1 min-h-[44px] rounded-lg border border-slate-300 font-semibold">{t("cancel")}</button>
              {!blocked && (
                <button onClick={doDelete} disabled={busy} data-testid="delete-confirm-btn"
                  className="flex-1 min-h-[44px] rounded-lg bg-red-600 text-white font-semibold disabled:opacity-50">{busy ? "…" : t("delete_permanently")}</button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Generic list for transactional modules + archived view + audit viewer.
export const ManagePanel = ({ module }) => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const load = () => api.get(`/admin/${module}`, { params: { include_archived: includeArchived, q, page, page_size: 25 } })
    .then(({ data }) => { setItems(data.items); setTotal(data.total); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [module, includeArchived, page]);
  useEffect(() => { const id = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(id); /* eslint-disable-next-line */ }, [q]);
  const label = (r) => r.name || r.business_name || r.contact_name || r.label || r.date || r.category || r.user_id || r.id;
  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")}
          data-testid={`manage-search-${module}`} className="flex-1 min-h-[44px] rounded-lg border border-slate-200 px-3" />
        <label className="flex items-center gap-1 text-sm text-slate-600 whitespace-nowrap">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid={`manage-archived-toggle-${module}`} /> {t("archived_records")}
        </label>
      </div>
      <div className="rounded-xl bg-white border border-slate-200 divide-y">
        {items.map((r) => (
          <div key={r.id} className={`flex items-center justify-between px-4 py-3 ${r.archived ? "opacity-60 bg-slate-50" : ""}`} data-testid={`manage-row-${module}-${r.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-slate-800 truncate">{label(r)} {r.archived && <span className="text-[10px] uppercase text-amber-600">({t("archive")})</span>}</p>
              <p className="text-xs text-slate-500 truncate">{[r.role, r.area, r.mobile, r.amount, r.status].filter(Boolean).join(" · ")}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <RowActions module={module} record={r} onDone={load} showRestore={!!r.archived} />
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-slate-400 py-8">{t("no_records")}</p>}
      </div>
      {total > 25 && (
        <div className="flex justify-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 rounded border disabled:opacity-40">‹</button>
          <span className="text-sm text-slate-500 py-1">{page} / {Math.ceil(total / 25)}</span>
          <button disabled={page >= Math.ceil(total / 25)} onClick={() => setPage(page + 1)} className="px-3 py-1 rounded border disabled:opacity-40">›</button>
        </div>
      )}
    </div>
  );
};

export const AuditPanel = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [module, setModule] = useState("");
  useEffect(() => { api.get("/admin/audit-logs", { params: { module: module || undefined, page_size: 100 } }).then(({ data }) => setItems(data.items)); }, [module]);
  return (
    <div className="space-y-3">
      <input value={module} onChange={(e) => setModule(e.target.value)} placeholder="Filter by module (e.g. user, product)"
        data-testid="audit-filter" className="w-full sm:w-64 min-h-[44px] rounded-lg border border-slate-200 px-3" />
      <div className="rounded-xl bg-white border border-slate-200 divide-y" data-testid="audit-list">
        {items.map((a) => (
          <div key={a.id} className="px-4 py-2 text-sm">
            <div className="flex justify-between gap-2">
              <span className="font-semibold text-slate-800">{a.action} · {a.entity}</span>
              <span className="text-xs text-slate-400">{a.at}</span>
            </div>
            <p className="text-xs text-slate-500">{a.details} — by {a.by} {a.reason ? `· ${t("reason")}: ${a.reason}` : ""}</p>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-slate-400 py-8">{t("no_records")}</p>}
      </div>
    </div>
  );
};

const ARCHIVE_MODULES = ["headquarters", "areas", "users", "doctors", "retailers", "stockists", "products", "options", "targets", "visits", "expenses", "followups", "sessions", "change_requests"];

export const ArchivedPanel = () => {
  const { t } = useLang();
  const [module, setModule] = useState("users");
  const [items, setItems] = useState([]);
  const load = () => api.get(`/admin/${module}`, { params: { include_archived: true, page_size: 200 } })
    .then(({ data }) => setItems((data.items || []).filter((r) => r.archived)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [module]);
  const label = (r) => r.name || r.business_name || r.contact_name || r.label || r.date || r.category || r.user_id || r.id;
  return (
    <div className="space-y-3">
      <select value={module} onChange={(e) => setModule(e.target.value)} data-testid="archived-module-select"
        className="w-full sm:w-64 min-h-[44px] rounded-lg border border-slate-200 px-3 bg-white capitalize">
        {ARCHIVE_MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <div className="rounded-xl bg-white border border-slate-200 divide-y">
        {items.map((r) => (
          <div key={r.id} className="flex items-center justify-between px-4 py-3" data-testid={`archived-row-${r.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-slate-800 truncate">{label(r)}</p>
              <p className="text-xs text-slate-500 truncate">{t("reason")}: {r.archive_reason} · {r.archived_by}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <RowActions module={module} record={r} onDone={load} showRestore />
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-center text-slate-400 py-8">{t("no_records")}</p>}
      </div>
    </div>
  );
};
